import { useState } from "react";
import { Check, ChevronRight, ArrowLeft, Phone, Wifi, Building2, Mail, User, Lock, Loader2 } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import { PBX_API_ORIGIN } from "@/lib/apiConfig";
import { SIGNUP } from "@/data/pageCopy";

const API = `${PBX_API_ORIGIN}/api/signup`;
const CONSOLE_URL = "https://console.ownpbx.in";

// Signing up costs nothing and asks for no card. The account is created, the
// customer picks the password they will sign in with, and everything that
// costs money — a plan, a number, wallet credit — is chosen inside the portal
// where the prices are in front of them. A checkout shown before anyone has
// seen the product is a checkout most people close.

// ── Step indicators ───────────────────────────────────────────────────────────
function Steps({ current }) {
  const steps = ["Product", "Your details"];
  return (
    <div className="flex items-center gap-0 mb-12">
      {steps.map((s, i) => {
        const done = i < current;
        const active = i === current;
        return (
          <div key={s} className="flex items-center">
            <div className="flex items-center gap-2">
              <div className={`w-7 h-7 flex items-center justify-center text-xs font-bold transition-colors ${
                done ? "bg-[#0047FF] text-white" : active ? "bg-neutral-900 text-white" : "bg-white/10 text-neutral-500"
              }`}>
                {done ? <Check size={13} /> : i + 1}
              </div>
              <span className={`text-sm font-medium hidden sm:block ${active ? "text-white" : done ? "text-[#5B8CFF]" : "text-neutral-400"}`}>{s}</span>
            </div>
            {i < steps.length - 1 && <div className={`w-8 sm:w-16 h-px mx-2 sm:mx-3 ${done ? "bg-[#0047FF]" : "bg-white/15"}`} />}
          </div>
        );
      })}
    </div>
  );
}

// ── Step 1: Product ───────────────────────────────────────────────────────────
const PRODUCTS = [
  {
    key: "pbx",
    label: "Cloud PBX",
    tag: null,
    desc: "Full-featured business phone system. Extensions, IVR, call routing, voicemail, recording.",
    icon: Phone,
    features: ["Multi-tenant architecture", "IVR & call routing", "Call recording & analytics", "Web softphone included"],
  },
  {
    key: "ai",
    label: "AI Voice Agent",
    tag: "AI",
    desc: "Autonomous AI agent that handles inbound & outbound calls 24/7 — no humans required.",
    icon: Wifi,
    highlight: true,
    features: ["Natural voice conversations", "Hindi, English & Hinglish", "Sentiment analysis", "CRM webhooks"],
  },
];

function StepProduct({ onNext }) {
  const [selected, setSelected] = useState(null);
  return (
    <div>
      <p className="font-mono-t text-xs uppercase tracking-[0.25em] text-[#5B8CFF] mb-4">Step 1</p>
      <h2 className="font-heading font-medium text-3xl md:text-4xl text-white tracking-tight mb-2">What do you need?</h2>
      <p className="text-neutral-500 mb-10">Both are free to set up. You pay for what you use, from inside your account.</p>

      <div className="grid md:grid-cols-2 gap-px bg-white/10 border border-white/10">
        {PRODUCTS.map((p) => {
          const Icon = p.icon;
          const active = selected === p.key;
          return (
            <button
              key={p.key}
              onClick={() => setSelected(p.key)}
              className={`relative text-left p-7 flex flex-col transition-all duration-200 cursor-pointer w-full ${
                p.highlight
                  ? active ? "bg-[#0047FF] text-white" : "bg-[#0a0a0a] text-white hover:bg-[#0047FF]"
                  : active ? "bg-neutral-900 text-white" : "bg-white/[0.02] text-white hover:bg-white/[0.05]"
              }`}
            >
              {active && <span className="absolute top-0 left-0 right-0 h-[3px] bg-[#0047FF]" />}
              {p.tag && (
                <span className={`self-start font-mono-t text-[10px] uppercase tracking-widest px-2 py-0.5 mb-4 ${
                  active ? "bg-white text-neutral-900" : p.highlight ? "bg-[#0047FF] text-white" : "bg-neutral-900 text-white"
                }`}>{p.tag}</span>
              )}
              <Icon size={24} className={`mb-4 ${active || p.highlight ? "opacity-90" : "text-neutral-400"}`} />
              <h3 className="font-heading font-semibold text-xl mb-2">{p.label}</h3>
              <p className={`text-sm leading-relaxed mb-6 flex-1 ${active || p.highlight ? "opacity-75" : "text-neutral-500"}`}>{p.desc}</p>
              <ul className="space-y-2">
                {p.features.map((f) => (
                  <li key={f} className={`flex items-start gap-2 text-sm ${active || p.highlight ? "opacity-90" : "text-neutral-300"}`}>
                    <Check size={14} className="mt-0.5 shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
            </button>
          );
        })}
      </div>

      <div className="mt-8 flex justify-end">
        <button
          onClick={() => selected && onNext(selected)}
          disabled={!selected}
          className="inline-flex items-center gap-2 bg-neutral-900 text-white font-bold px-8 py-4 hover:bg-neutral-700 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
        >
          Continue <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
}

// India is billed in rupees, everywhere else in dollars. The server holds the
// same mapping and decides for real; this is what the customer is shown so
// the choice is not made behind their back.
const REGIONS = [
  { key: "IN", label: "India", currency: "₹ INR" },
  { key: "US", label: "United States", currency: "$ USD" },
  { key: "OTHER", label: "Other country", currency: "$ USD" },
];
const CURRENCY_OF = { IN: "Indian rupees (₹)", US: "US dollars ($)", OTHER: "US dollars ($)" };

// ── Step 2: Details ───────────────────────────────────────────────────────────
function StepDetails({ product, onDone, onBack }) {
  const [form, setForm] = useState({
    company_name: "", admin_email: "", admin_name: "", admin_phone: "",
    password: "", confirm: "", number_option: "byoc", region: "IN",
  });
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Said while they type rather than after a round trip: a password refused
  // by the server once the spinner has been going feels like a broken form.
  const pwShort = form.password.length > 0 && form.password.length < 10;
  const pwMismatch = form.confirm.length > 0 && form.confirm !== form.password;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!consent) { setError("Please accept the Privacy Policy to continue."); return; }
    if (form.password !== form.confirm) { setError("The two passwords do not match."); return; }
    setLoading(true); setError("");
    try {
      const res = await fetch(`${API}/register`, {
        method: "POST", mode: "cors",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          product,
          company_name: form.company_name,
          admin_email: form.admin_email,
          admin_name: form.admin_name,
          admin_phone: form.admin_phone,
          password: form.password,
          number_option: form.number_option,
          region: form.region,
        }),
      });
      // A 404 here means this page has shipped ahead of the API that serves
      // it, which is a deploy-order problem and not something the customer
      // did. Say so plainly instead of showing them a parse error.
      if (res.status === 404) {
        throw new Error(
          "Sign-ups are being updated right now and will be back in a few minutes. " +
          "If you need an account today, email hello@ownpbx.in and we will set it up."
        );
      }
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "We could not create your account. Please try again.");
      onDone(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const field = "w-full pl-10 pr-4 py-3 border border-white/15 bg-white/[0.03] text-white text-sm focus:outline-none focus:border-white transition-colors";

  return (
    <div>
      <p className="font-mono-t text-xs uppercase tracking-[0.25em] text-[#5B8CFF] mb-4">Step 2</p>
      <h2 className="font-heading font-medium text-3xl md:text-4xl text-white tracking-tight mb-2">Your details</h2>
      <p className="text-neutral-500 mb-10">No card, no charge. You will be signing in to your account in about a minute.</p>

      <form onSubmit={handleSubmit} className="max-w-xl space-y-5">
        {/* Where they are decides what they are billed in, and the plan they
            land on is one priced in that same currency. Nothing is converted
            anywhere, so this is the moment the choice is made. */}
        <div>
          <label className="block text-sm font-semibold text-neutral-300 mb-3">Where are you based?</label>
          <div className="grid grid-cols-3 gap-3">
            {REGIONS.map((r) => {
              const active = form.region === r.key;
              return (
                <button key={r.key} type="button" onClick={() => set("region", r.key)}
                  className={`p-4 border text-left transition-colors ${active ? "border-neutral-900 bg-neutral-900 text-white" : "border-white/15 bg-white/[0.02] text-white hover:border-white/40"}`}>
                  <div className="font-semibold text-sm mb-1">{r.label}</div>
                  <div className={`text-xs ${active ? "text-neutral-300" : "text-neutral-500"}`}>Billed in {r.currency}</div>
                </button>
              );
            })}
          </div>
          <p className="mt-2 text-xs text-neutral-400">
            Your wallet, your rates and your invoices will all be in {CURRENCY_OF[form.region]}.
          </p>
        </div>

        <div>
          <label className="block text-sm font-semibold text-neutral-300 mb-1.5">Company / Business name *</label>
          <div className="relative">
            <Building2 size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400" />
            <input required value={form.company_name} onChange={(e) => set("company_name", e.target.value)}
              className={field} placeholder="Acme Corp" />
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-neutral-300 mb-1.5">Work email *</label>
          <div className="relative">
            <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400" />
            <input required type="email" value={form.admin_email} onChange={(e) => set("admin_email", e.target.value)}
              className={field} placeholder="you@company.com" autoComplete="email" />
          </div>
          <p className="mt-1.5 text-xs text-neutral-400">This is the email you will sign in with.</p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-neutral-300 mb-1.5">Your name</label>
            <div className="relative">
              <User size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400" />
              <input value={form.admin_name} onChange={(e) => set("admin_name", e.target.value)}
                className={field} placeholder="Ashish Kumar" autoComplete="name" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-neutral-300 mb-1.5">Phone number</label>
            <div className="relative">
              <Phone size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400" />
              <input value={form.admin_phone} onChange={(e) => set("admin_phone", e.target.value)}
                className={field} placeholder="+91 98765 43210" autoComplete="tel" />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-neutral-300 mb-1.5">Password *</label>
            <div className="relative">
              <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400" />
              <input required type="password" value={form.password} onChange={(e) => set("password", e.target.value)}
                className={field} placeholder="At least 10 characters" autoComplete="new-password" minLength={10} />
            </div>
            {pwShort && <p className="mt-1.5 text-xs text-amber-400">At least 10 characters.</p>}
          </div>
          <div>
            <label className="block text-sm font-semibold text-neutral-300 mb-1.5">Confirm password *</label>
            <div className="relative">
              <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400" />
              <input required type="password" value={form.confirm} onChange={(e) => set("confirm", e.target.value)}
                className={field} placeholder="Type it again" autoComplete="new-password" />
            </div>
            {pwMismatch && <p className="mt-1.5 text-xs text-amber-400">These do not match.</p>}
          </div>
        </div>

        {/* Two answers, not a catalogue. Which number they actually take, and
            what it costs, is a decision for the far side of the login. */}
        <div>
          <label className="block text-sm font-semibold text-neutral-300 mb-3">How will calls reach you?</label>
          <div className="grid grid-cols-2 gap-3">
            <button type="button" onClick={() => set("number_option", "byoc")}
              className={`p-4 border text-left transition-colors ${form.number_option === "byoc" ? "border-neutral-900 bg-neutral-900 text-white" : "border-white/15 bg-white/[0.02] text-white hover:border-white/40"}`}>
              <div className="font-semibold text-sm mb-1">I have my own carrier</div>
              <div className={`text-xs ${form.number_option === "byoc" ? "text-neutral-300" : "text-neutral-500"}`}>Connect your existing SIP trunk</div>
            </button>
            <button type="button" onClick={() => set("number_option", "from_us")}
              className={`p-4 border text-left transition-colors ${form.number_option === "from_us" ? "border-neutral-900 bg-neutral-900 text-white" : "border-white/15 bg-white/[0.02] text-white hover:border-white/40"}`}>
              <div className="font-semibold text-sm mb-1">I need a number from OwnPBX</div>
              <div className={`text-xs ${form.number_option === "from_us" ? "text-neutral-300" : "text-neutral-500"}`}>Pick one in your account after signing in</div>
            </button>
          </div>
        </div>

        <label className="flex items-start gap-2.5">
          <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)}
            className="mt-0.5 shrink-0 accent-white" data-testid="signup-consent" />
          <span className="text-[13px] leading-relaxed text-neutral-400">
            I agree that OwnPBX may store and process my company and contact details to create and run this account,
            as described in the{" "}
            <a href="/privacy" target="_blank" rel="noreferrer" className="underline hover:text-white">Privacy Policy</a>{" "}
            and{" "}
            <a href="/terms" target="_blank" rel="noreferrer" className="underline hover:text-white">Terms of Service</a>.
          </span>
        </label>

        {error && <div className="p-4 bg-red-500/10 border border-red-500/30 text-red-400 text-sm">{error}</div>}

        <div className="flex justify-between pt-2">
          <button type="button" onClick={onBack} className="inline-flex items-center gap-2 text-neutral-400 hover:text-white font-medium transition-colors">
            <ArrowLeft size={16} /> Back
          </button>
          <button type="submit" disabled={loading || !consent}
            className="inline-flex items-center gap-2 bg-[#0047FF] text-white font-bold px-10 py-4 hover:bg-blue-700 transition-colors disabled:opacity-50">
            {loading ? <><Loader2 size={16} className="animate-spin" /> Creating your account…</> : <>Create account <ChevronRight size={16} /></>}
          </button>
        </div>
      </form>
    </div>
  );
}

// ── Done ──────────────────────────────────────────────────────────────────────
function SuccessScreen({ email, wantsNumber, loginUrl }) {
  const next = [
    "Sign in with the email and password you just chose",
    wantsNumber ? "Pick your number under Numbers" : "Connect your SIP trunk under Trunks",
    "Add credit under My Account, and start making calls",
  ];
  return (
    <div className="text-center py-12">
      <div className="w-16 h-16 bg-[#0047FF] flex items-center justify-center mx-auto mb-6">
        <Check size={32} className="text-white" />
      </div>
      <h2 className="font-heading font-medium text-3xl md:text-4xl text-white tracking-tight mb-4">Your account is ready.</h2>
      <p className="text-neutral-400 text-lg mb-10">
        Sign in as <strong className="text-white">{email}</strong> with the password you just chose.
      </p>

      <div className="max-w-md mx-auto text-left border border-white/10 bg-white/[0.02] px-6 py-5 mb-8">
        <h4 className="font-semibold text-sm text-white mb-3">What to do next</h4>
        <ul className="space-y-2">
          {next.map((s, i) => (
            <li key={s} className="flex items-start gap-2.5 text-sm text-neutral-400">
              <span className="w-5 h-5 bg-[#0047FF] text-white text-xs flex items-center justify-center font-bold shrink-0">{i + 1}</span>
              {s}
            </li>
          ))}
        </ul>
      </div>

      <a href={loginUrl || CONSOLE_URL}
        className="inline-flex items-center gap-2 bg-neutral-900 text-white font-bold px-8 py-4 hover:bg-neutral-700 transition-colors">
        Go to sign in <ChevronRight size={16} />
      </a>
    </div>
  );
}

// ── Main SignupPage ───────────────────────────────────────────────────────────
export default function SignupPage() {
  const [step, setStep] = useState(0);
  const [product, setProduct] = useState(null);
  const [done, setDone] = useState(null);

  if (done) return (
    <div className="min-h-screen bg-[#0a0a0a] pt-32 pb-24">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        <SuccessScreen email={done.email} wantsNumber={done.wants_number} loginUrl={done.login_url} />
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <div className="border-b border-white/10 bg-white/[0.02] pt-28 pb-8">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <Reveal>
            <p className="font-mono-t text-xs uppercase tracking-[0.25em] text-[#5B8CFF] mb-3">{SIGNUP.kicker}</p>
            <h1 className="font-heading font-medium text-4xl md:text-5xl text-white tracking-tight">{SIGNUP.h1}</h1>
          </Reveal>
        </div>
      </div>

      <div className="mx-auto max-w-[1400px] px-6 md:px-12 py-14">
        <Steps current={step} />
        {step === 0 && <StepProduct onNext={(p) => { setProduct(p); setStep(1); }} />}
        {step === 1 && <StepDetails product={product} onDone={setDone} onBack={() => setStep(0)} />}
      </div>
    </div>
  );
}
